import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { renderQrCodeDataUrl } from '@/utils/barcodeRenderer';
import { validateDataUrl } from '@/utils/sanitizeHtml';

export type LabelType = 'kiosk' | 'shelf' | 'box';
export type ConditionCode = 'A' | 'B' | 'H';

export interface BarcodeLabelBulkPrintItem {
  id: string;
  barcode_value: string;
  location_name: string;
  location_path: string;
  catalog_name: string;
  lin?: string | null;
  old_lin?: string | null;
  nsn?: string | null;
  color?: string | null;
  unit_of_issue?: string | null;
  part_number?: string | null;
  warehouse_location_name?: string | null;
  storefront_location_name?: string | null;
}

interface BarcodeLabelBulkPrintDialogProps {
  open: boolean;
  onClose: () => void;
  items: BarcodeLabelBulkPrintItem[];
}

type RenderedLabel = BarcodeLabelBulkPrintItem & {
  qrDataUrl: string | null;
};

export const BarcodeLabelBulkPrintDialog: React.FC<BarcodeLabelBulkPrintDialogProps> = ({
  open,
  onClose,
  items,
}) => {
  const [rendered, setRendered] = useState<RenderedLabel[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [labelType, setLabelType] = useState<LabelType>('shelf');
  const [conditionCode, setConditionCode] = useState<ConditionCode>('A');

  const escapeHtml = useMemo(() => {
    return (value: string) =>
      value
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#39;');
  }, []);

  useEffect(() => {
    if (!open) {
      setRendered(null);
      setError(null);
      setLabelType('shelf');
      setConditionCode('A');
      return;
    }

    let cancelled = false;

    (async () => {
      setRendered(null);
      setError(null);

      try {
        const renderedLabels = await Promise.all(
          items.map(async (item) => {
            const qrDataUrl = await renderQrCodeDataUrl(item.barcode_value);
            return {
              ...item,
              qrDataUrl,
            };
          }),
        );

        if (!cancelled) {
          setRendered(renderedLabels);
        }
      } catch (err) {
        if (!cancelled) {
          setError('Failed to render one or more labels.');
        }
        console.error('Bulk label render failed', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, items]);

  const handlePrint = () => {
    if (!rendered || rendered.length === 0) return;

    const printWindow = window.open('', '_blank', 'width=900,height=720');
    if (!printWindow) {
      setError('Pop-up blocked. Allow pop-ups to print labels.');
      return;
    }

    const baseUrl = window.location.origin;
    const logoSrc = labelType === 'kiosk' ? `${baseUrl}/q-logo.png` : `${baseUrl}/equip-logo.png`;

    const labelsHtml = rendered
      .map((label) => {
        const catalogName = escapeHtml(label.catalog_name);
        const barcodeValue = escapeHtml(label.barcode_value);
        const locationName = escapeHtml(label.location_name);
        const lin = label.lin ? escapeHtml(label.lin) : 'N/A';
        const oldLin = label.old_lin ? escapeHtml(label.old_lin) : null;
        const nsn = label.nsn ? escapeHtml(label.nsn) : 'N/A';
        const color = label.color ? escapeHtml(label.color) : 'N/A';
        const uoi = label.unit_of_issue ? escapeHtml(label.unit_of_issue) : 'EA';
        const warehouseLocationName = label.warehouse_location_name ? escapeHtml(label.warehouse_location_name) : null;
        const storefrontLocationName = label.storefront_location_name ? escapeHtml(label.storefront_location_name) : null;
        const partNumber = label.part_number ? escapeHtml(label.part_number) : null;
        const validatedQrUrl = validateDataUrl(label.qrDataUrl);

        if (labelType === 'kiosk') {
          return `
            <div class="label kiosk-label">
              <div class="qr-container">
                <img src="${validatedQrUrl}" class="qr-img" alt="QR" />
                <img src="${logoSrc}" class="qr-logo" alt="Logo" />
              </div>
              <div class="item-name">${catalogName}</div>
              ${partNumber ? `<div class="part-num">PN: ${partNumber}</div>` : ''}
            </div>
          `;
        }

        if (labelType === 'shelf') {
          const metaParts: string[] = [];
          if (lin !== 'N/A') metaParts.push(`LIN: ${lin}`);
          if (nsn !== 'N/A') metaParts.push(`NSN: ${nsn}`);
          return `
            <div class="label shelf-label">
              <div class="qr-container">
                <img src="${validatedQrUrl}" class="qr-img" alt="QR" />
                <img src="${logoSrc}" class="qr-logo" alt="Logo" />
              </div>
              <div class="text-container">
                <div class="shelf-meta">${metaParts.join(' | ')}</div>
                <div class="item-name">${catalogName}</div>
                <div class="shelf-location">${locationName}</div>
                <div class="barcode-val">${barcodeValue}</div>
              </div>
            </div>
          `;
        }

        if (labelType === 'box') {
          const subLocParts: string[] = [];
          if (warehouseLocationName) subLocParts.push(`Warehouse: ${warehouseLocationName}`);
          if (storefrontLocationName) subLocParts.push(`Storefront: ${storefrontLocationName}`);
          const subLocHtml = subLocParts.length > 0
            ? `<div class="box-sub-locations">${subLocParts.join(' | ')}</div>`
            : '';

          return `
            <div class="label box-label">
              <div class="box-header">
                <div>LIN: ${lin}${oldLin ? ` (Old LIN: ${oldLin})` : ''}</div>
                <div>NSN: ${nsn}</div>
              </div>
              <div class="box-title">${catalogName}</div>
              <div class="box-details">
                <strong>UI:</strong> ${uoi} &nbsp;|&nbsp; 
                <strong>Color:</strong> ${color} &nbsp;|&nbsp; 
                <strong>Condition:</strong> ${escapeHtml(conditionCode)}
              </div>
              <div class="box-location">Location: ${locationName}</div>
              ${subLocHtml}
              
              <div class="qr-container">
                <img src="${validatedQrUrl}" class="qr-img" alt="QR" />
                <img src="${logoSrc}" class="qr-logo" alt="Logo" />
              </div>

              <table class="tracking-grid">
                <thead>
                  <tr>
                    <th style="width: 30%">BOX QTY</th>
                    <th style="width: 40%">DATE</th>
                    <th style="width: 30%">INITIALS</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td></td><td></td><td></td></tr>
                  <tr><td></td><td></td><td></td></tr>
                  <tr><td></td><td></td><td></td></tr>
                  <tr><td></td><td></td><td></td></tr>
                  <tr><td></td><td></td><td></td></tr>
                </tbody>
              </table>
            </div>
          `;
        }

        return '';
      })
      .join('\n');

    printWindow.document.write(`<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <title>Print Barcode Labels</title>
  <style>
    body {
      font-family: Arial, sans-serif;
      margin: 0;
      padding: 0;
      color: #000;
    }
    
    .label {
      box-sizing: border-box;
      page-break-inside: avoid;
      overflow: hidden;
    }

    /* Box labels must not clip content - table rows need to be fully visible */
    .box-label {
      overflow: visible;
    }

    /* Kiosk Label (1x1) */
    .kiosk-label {
      width: 1in;
      height: 1in;
      display: inline-flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 0.05in;
      margin: 0.1in; /* screen spacing */
      border: 1px dashed #ccc; /* screen border */
    }
    .kiosk-label .qr-container { position: relative; width: 0.7in; height: 0.7in; }
    .kiosk-label .item-name { 
      font-size: 5.5pt; 
      font-weight: bold; 
      text-align: center; 
      line-height: 1.1; 
      overflow: hidden; 
      max-height: 0.2in; 
      width: 100%; 
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      margin-top: 0.02in; 
    }
    .kiosk-label .part-num {
      font-size: 5pt;
      text-align: center;
      color: #000;
      line-height: 1.1;
      width: 100%;
    }
    
    /* Shelf Label (2x1) */
    .shelf-label {
      width: 2in;
      height: 1in;
      display: inline-flex;
      flex-direction: row;
      align-items: stretch;
      justify-content: flex-start;
      padding: 0.05in;
      margin: 0.1in;
      border: 1px dashed #ccc;
    }
    .shelf-label .qr-container { position: relative; height: 100%; aspect-ratio: 1; flex-shrink: 0; display: flex; align-items: center; justify-content: center; }
    .shelf-label .text-container { margin-left: 0.05in; display: flex; flex-direction: column; justify-content: center; flex: 1; overflow: hidden; }
    .shelf-label .shelf-meta { font-size: 6pt; color: #000; line-height: 1.2; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .shelf-label .item-name { font-size: 7.5pt; font-weight: bold; line-height: 1.2; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
    .shelf-label .shelf-location { font-size: 6pt; color: #000; line-height: 1.2; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .shelf-label .barcode-val { font-size: 5.5pt; font-family: monospace; color: #000; margin-top: 0.01in; }
    
    /* Box Label (Full Page) */
    .box-label {
      width: 100vw;
      min-height: 100vh;
      display: flex;
      flex-direction: column;
      align-items: stretch;
      justify-content: flex-start;
      padding: 0.5in 1in;
      box-sizing: border-box;
      margin-bottom: 20px;
      border: 1px dashed #ccc;
      page-break-after: always;
    }
    .box-label .box-header { display: flex; justify-content: space-between; font-size: 24pt; font-weight: bold; border-bottom: 2px solid #000; padding-bottom: 10px; margin-bottom: 20px; }
    .box-label .box-title { font-size: 36pt; font-weight: bold; text-align: center; margin: 30px 0; line-height: 1.2; }
    .box-label .box-details { font-size: 24pt; text-align: center; margin-bottom: 30px; }
    .box-label .box-location { font-size: 20pt; text-align: center; color: #000; margin-bottom: 10px; }
    .box-label .box-sub-locations { font-size: 14pt; text-align: center; color: #000; margin-bottom: 40px; }
    .box-label .qr-container { position: relative; width: 350px; height: 350px; margin: 0 auto 50px auto; }
    .box-label .tracking-grid { width: 100%; border-collapse: collapse; margin-top: auto; }
    .box-label .tracking-grid th, .box-label .tracking-grid td { border: 2px solid #000; padding: 15px; text-align: center; font-size: 18pt; }
    .box-label .tracking-grid th { background-color: #f0f0f0; }
    .box-label .tracking-grid td { height: 100px; }

    /* Shared QR Logo Overlay */
    .qr-img { width: 100%; height: 100%; display: block; object-fit: contain; }
    .qr-logo { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); background: white; padding: 2%; border-radius: 4px; }
    .kiosk-label .qr-logo { width: 22%; }
    .shelf-label .qr-logo { width: 35%; }
    .box-label .qr-logo { width: 20%; padding: 2%; }

    @media print {
      @page {
        margin: 0;
        size: ${labelType === 'kiosk' ? '1in 1in' : labelType === 'shelf' ? '2in 1in' : 'letter'};
      }
      .label { margin: 0; border: none; }
      
      /* Ensure each box label fits exactly on one letter page.
         Content is sized down so it fits within 10in (11in - 1in total padding).
         overflow: hidden matches the single-print behaviour (position:fixed + height:100vh). */
      .box-label {
        padding: 0.5in 1in;
        height: 11in;
        overflow: hidden;
        box-sizing: border-box;
      }
      .box-label .box-header { font-size: 18pt; }
      .box-label .box-title { font-size: 28pt; margin: 16px 0; }
      .box-label .box-details { font-size: 18pt; margin-bottom: 16px; }
      .box-label .box-location { font-size: 16pt; margin-bottom: 8px; }
      .box-label .box-sub-locations { font-size: 12pt; margin-bottom: 20px; }
      .box-label .qr-container { width: 260px; height: 260px; margin: 0 auto 24px auto; }
      .box-label .tracking-grid td { height: 60px; }
      .box-label .tracking-grid th, .box-label .tracking-grid td { font-size: 14pt; padding: 8px; }
      
      /* Display small labels block/inline cleanly depending on printer behavior */
      .kiosk-label, .shelf-label {
        display: flex;
        page-break-after: always;
      }
      
      /* Kiosk label print overrides: tighten padding so QR + text fit in 1in without clipping */
      .kiosk-label {
        padding: 0.02in;
      }
      .kiosk-label .item-name {
        max-height: none;
        margin-top: 0.01in;
        -webkit-line-clamp: unset;
      }
    }
  </style>
</head>
<body>
  ${labelsHtml}
  <script>
    // Wait briefly for images to load before printing
    window.addEventListener('load', () => {
      setTimeout(() => {
        window.focus();
        window.print();
        // Optional: auto-close after print dialogue
        // window.close();
      }, 500);
    });
  </script>
</body>
</html>`);
    printWindow.document.close();
  };

  const count = items.length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Print Barcode Labels ({count})</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Label Type</InputLabel>
              <Select
                value={labelType}
                label="Label Type"
                onChange={(e) => setLabelType(e.target.value as LabelType)}
              >
                <MenuItem value="kiosk">Kiosk Label (1x1&quot;)</MenuItem>
                <MenuItem value="shelf">Shelf Label (1x2&quot;)</MenuItem>
                <MenuItem value="box">Warehouse Box Label (Full Page)</MenuItem>
              </Select>
            </FormControl>

            {labelType === 'box' && (
              <FormControl fullWidth size="small">
                <InputLabel>Condition Code</InputLabel>
                <Select
                  value={conditionCode}
                  label="Condition Code"
                  onChange={(e) => setConditionCode(e.target.value as ConditionCode)}
                >
                  <MenuItem value="A">Condition A</MenuItem>
                  <MenuItem value="B">Condition B</MenuItem>
                  <MenuItem value="H">Condition H</MenuItem>
                </Select>
              </FormControl>
            )}
          </Stack>

          {error && <Alert severity="error">{error}</Alert>}
          {!error && !rendered && <Typography variant="body2">Rendering labels…</Typography>}
          {rendered && (
            <Typography variant="body2" color="text.secondary">
              Ready to print {count} labels in {labelType} format.
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          onClick={handlePrint}
          variant="contained"
          disabled={!rendered || rendered.length === 0}
        >
          Print All
        </Button>
      </DialogActions>
    </Dialog>
  );
};
